import React, { FC, memo } from 'react';
import ScenarioSwitch from '@components/Headers/core/ScenarioSwitch';
import { useProject } from '@context/Project/ProjectProvider';
import { Box, Divider, Stack } from '@mui/material';
import { themePalette } from '@theme/muiTheme';
import { AdditionalSettings } from '@views/Project/components/AdditionalSettings/AdditionalSettings';
import ModelFiltersPopover from '@views/Project/components/ModelFiltersPopover';
import DashboardFilters from '@views/Project/components/Toolbox/components/DashboardFilters';
import LinkButton from '@views/Project/components/Toolbox/components/LinkButton';
import ModesDropdown from '@views/Project/components/Toolbox/components/ModesDropdown';
import useToolboxLinks from '@views/Project/components/Toolbox/hooks/useToolboxLinks';
import { LinksWrapper } from '@views/Project/components/Toolbox/Toolbox.styled';

/**
 * The project toolbox row in AC1's order: mode selector, the view tabs, the
 * Filters funnel with the applied-filter chips slot beside it, then the kebab
 * and the scenario switch on the far right. The funnel is the only entry
 * point to model filtering; the Dashboard view keeps its own filters control
 * in the same slot. The chips slot is an empty box that QSP-3573 mounts into.
 */
const Toolbox: FC = () => {
  const { isVersionsShown, isDashboardMode } = useProject();
  const isProjectLinksDisabled = isVersionsShown;

  const { toolboxLinks, handleLinkClick } = useToolboxLinks();

  return (
    <Stack
      p={6}
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      borderBottom={`1px solid ${themePalette.grey[500]}`}
    >
      <Stack gap={4} direction="row" alignItems="center">
        <ModesDropdown />

        <Divider orientation="vertical" flexItem />

        <LinksWrapper $disabled={isProjectLinksDisabled}>
          {toolboxLinks.map((linkItem) => (
            <LinkButton
              key={linkItem.label}
              item={linkItem}
              onClick={(e) => handleLinkClick(e, linkItem.to)}
            />
          ))}
        </LinksWrapper>

        <Divider orientation="vertical" flexItem />

        {isDashboardMode ? <DashboardFilters /> : <ModelFiltersPopover />}

        <Box
          id="applied-filter-chips"
          display="flex"
          alignItems="center"
          gap={1}
        />
      </Stack>

      <Stack gap={4} direction="row" alignItems="center">
        <AdditionalSettings />

        <ScenarioSwitch />
      </Stack>
    </Stack>
  );
};

export default memo(Toolbox);
